import React from 'react';
import { CheckCircle, AlertCircle, UploadCloud, Loader2, Minus, HelpCircle } from 'lucide-react';
import { KsefStatus, KsefEnvironment, KSEF_ENV_META } from '../../../api/ksef';

const STATUS_CONFIG: Record<
  KsefStatus,
  { label: string; cls: string; icon: React.ReactNode }
> = {
  none: {
    label: 'Not Submitted',
    cls: 'bg-stone-100 text-stone-600 dark:bg-stone-800 dark:text-stone-300',
    icon: <UploadCloud className="w-3.5 h-3.5" />,
  },
  processing: {
    label: 'Processing',
    cls: 'bg-coral-100 text-coral-700 dark:bg-coral-900/40 dark:text-foreground-muted',
    icon: <Loader2 className="w-3.5 h-3.5 animate-spin" />,
  },
  accepted: {
    label: 'Accepted',
    cls: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
    icon: <CheckCircle className="w-3.5 h-3.5" />,
  },
  rejected: {
    label: 'Rejected',
    cls: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
    icon: <AlertCircle className="w-3.5 h-3.5" />,
  },
  na: {
    label: 'Not Applicable',
    cls: 'bg-stone-100 text-stone-500 dark:bg-stone-800 dark:text-stone-400',
    icon: <Minus className="w-3.5 h-3.5" />,
  },
};

export const KsefChip: React.FC<{ status: KsefStatus; size?: 'sm' | 'md' }> = ({
  status,
  size = 'md',
}) => {
  const c = STATUS_CONFIG[status] || STATUS_CONFIG.none;
  const pad = size === 'sm' ? 'px-2 py-0.5 text-[11px]' : 'px-2.5 py-1 text-xs';
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full font-semibold ${pad} ${c.cls}`}
    >
      {c.icon}
      {c.label}
    </span>
  );
};

/**
 * Environment tones. Production is amber — "pay attention, this is real" — not
 * red: sending a live invoice is the product working correctly, not an error.
 * It is no longer green because green reads as "nothing to think about" on the
 * one environment with legal consequences. Green stays on the accepted state.
 *
 * Every variant carries an explicit dark-mode pair; the previous map was
 * light-only, so the badges washed out in the dark theme the app ships with.
 */
const ENV_TONE_CLASS: Record<'neutral' | 'info' | 'attention', string> = {
  neutral:
    'bg-stone-100 text-stone-600 border-stone-300 '
    + 'dark:bg-stone-800 dark:text-stone-300 dark:border-stone-600',
  info:
    'bg-sky-50 text-sky-700 border-sky-200 '
    + 'dark:bg-sky-900/40 dark:text-sky-300 dark:border-sky-800',
  attention:
    'bg-amber-50 text-amber-700 border-amber-300 '
    + 'dark:bg-amber-900/40 dark:text-amber-300 dark:border-amber-700',
};

const UNKNOWN_ENV_CLASS =
  'bg-transparent text-stone-500 border-stone-400 border-dashed '
  + 'dark:text-stone-400 dark:border-stone-500';

/**
 * Renders which KSeF environment an action targets.
 *
 * An unresolvable environment renders "Environment unknown" — it must never
 * fall back to "Test", which is what made a live production submission look
 * like a sandbox one.
 */
export const KsefEnvBadge: React.FC<{ env: KsefEnvironment | null | undefined }> = ({ env }) => {
  const base =
    'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-semibold';

  if (!env) {
    return (
      <span className={`${base} ${UNKNOWN_ENV_CLASS}`} title="ViewPoint could not determine which KSeF environment this seller is connected to.">
        <HelpCircle className="w-3 h-3" />
        Environment unknown
      </span>
    );
  }

  const meta = KSEF_ENV_META[env];
  return (
    <span className={`${base} ${ENV_TONE_CLASS[meta.tone]}`} title={meta.legal}>
      <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70" />
      {meta.label}
    </span>
  );
};
